import React, { useCallback, useEffect, useId, useState } from "react";
import { NotebookPen } from "lucide-react";
import { Button } from "../../../components/new-components/ui/button";
import { Textarea } from "../../../components/new-components/ui/textarea";
import { Input } from "../../../components/new-components/ui/input";
import { VisitNotePage, visitNotesApi } from "./visitNotesApi";

/**
 * Visit note (facility records Phase 0c; Isaiah Cotto, 2026-10-27): the note the PROVIDER writes for this resident on
 * today's visit to this building. It is what the facility receives when the provider closes out the visit (with the
 * resident's audiogram). Not the Manifest notes, never a private note. An edit is a new version; the latest is sent.
 * "No note needed" takes a one-line reason the office sees. Phone first: one text box, quick picks that fill it.
 * Hidden entirely when the server does not serve it (before its migration, or for someone who may not write it).
 */
export interface VisitNoteProps {
  facilityId: string;
  patientId: string;
  canWrite: boolean;
}

const when = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });

export function VisitNote({ facilityId, patientId, canWrite }: VisitNoteProps) {
  const ids = useId();
  const [page, setPage] = useState<VisitNotePage | null>(null);
  const [hidden, setHidden] = useState(false);
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState<"note" | "none">("note");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await visitNotesApi.get(facilityId, patientId);
      // Only an answer of this shape is a visit-note page (an older server, or anything else, draws nothing).
      if (r.success && r.data && r.data.ready === true && Array.isArray(r.data.quick_picks)) {
        setPage(r.data);
        if (r.data.note && r.data.note.kind === "NOTE") setText(r.data.note.body);
      } else setHidden(true);
    } catch { setHidden(true); }
  }, [facilityId, patientId]);
  useEffect(() => { setPage(null); setHidden(false); setText(""); setReason(""); setStatus(""); setError(""); load(); }, [load]);
  if (!canWrite || hidden || !page) return null;

  const latest = page.note;
  const save = async () => {
    setBusy(true); setError(""); setStatus("");
    try {
      const r = mode === "note" ? await visitNotesApi.write(facilityId, patientId, text) : await visitNotesApi.noNoteNeeded(facilityId, patientId, reason);
      if (r.success && r.data) {
        setPage({ ...page, note: r.data.note });
        setStatus(r.data.changed ? (mode === "note" ? "Visit note saved." : "Marked: no note needed.") : "Already saved; nothing changed.");
        if (mode === "none") setReason("");
      } else setError(r.message || r.error || "The visit note was not saved.");
    } catch { setError("eMerge did not confirm the save. Try again; the same text is never saved twice."); } finally { setBusy(false); }
  };
  const textId = `${ids}-text`;
  return (
    <section className="fw-private-notes" aria-labelledby={`${ids}-h`} data-testid="visit-note">
      <h3 id={`${ids}-h`} className="fw-replacement-title"><NotebookPen size={14} aria-hidden="true" /> Visit note</h3>
      <p className="fw-muted">What the facility receives for today&rsquo;s visit, sent when you close out the visit. Not the manifest notes, never your private notes.</p>
      {latest && (
        <p className="fw-muted" role="status">
          {latest.kind === "NOTE" ? `Saved (version ${latest.version})` : `No note needed: ${latest.body}`}
          {" · "}{(latest.written_by || latest.by).mine ? "you" : (latest.written_by || latest.by).first_name}
          {latest.edited_by ? `, edited by ${latest.by.mine ? "you" : latest.edited_by.first_name}` : ""} at {when(latest.at)}
        </p>
      )}
      <div className="fw-actions" role="group" aria-label="Note or no note">
        <Button type="button" variant={mode === "note" ? "default" : "outline"} onClick={() => setMode("note")}>Write a note</Button>
        <Button type="button" variant={mode === "none" ? "default" : "outline"} onClick={() => setMode("none")}>No note needed</Button>
      </div>
      {mode === "note" ? (
        <>
          <div className="fw-actions" aria-label="Quick picks">
            {page.quick_picks.map((q) => (
              <Button key={q} type="button" variant="outline" size="sm" onClick={() => setText(q)}>{q}</Button>
            ))}
          </div>
          <label htmlFor={textId} className="fw-muted">Note for this visit</label>
          <Textarea id={textId} value={text} maxLength={page.max} rows={4} onChange={(e) => { setText(e.target.value); setStatus(""); }} disabled={busy} />
          <p className="fw-muted">{text.length} of {page.max} characters</p>
        </>
      ) : (
        <>
          <label htmlFor={`${ids}-reason`} className="fw-muted">Why no note is needed (the office sees this)</label>
          <Input id={`${ids}-reason`} value={reason} maxLength={200} onChange={(e) => { setReason(e.target.value); setStatus(""); }} disabled={busy} />
        </>
      )}
      <div className="fw-actions">
        <Button type="button" onClick={save} disabled={busy || (mode === "note" ? !text.trim() : reason.trim().length < 3)}>
          {busy ? "Saving…" : mode === "note" ? "Save visit note" : "Save: no note needed"}
        </Button>
      </div>
      {error && <p role="alert" className="fw-error">{error}</p>}
      <p role="status" className="fw-save-status">{status}</p>
    </section>
  );
}
export default VisitNote;
